// ===========================================================================
// Care gaps revisados en Pre-Visit Planning — store local de la alerta.
//
// Cuando el Medical Assistant marca "Open Care Gaps Review" como Complete en
// el checklist de preparación, la revisión tiene que llegarle a quien atiende:
// el médico no abre Pre-Visit Planning, abre el encuentro. Por eso la señal
// viaja por el MISMO panel de alertas del layout que usan las notas sin firmar
// y las observaciones de billing — un solo indicador, no tres compitiendo.
//
// Solo se registra si el paciente TIENE care gaps abiertos: "revisado y no hay
// nada" no es una alerta, es ruido.
//
// TODO(backend): hoy el backend de revisión (TAB_APPOINTMENT_REV) solo guarda
// el booleano `reviewed`; cuando persista el detalle del checklist, este store
// se reemplaza por la lectura real manteniendo la forma de retorno.
// ===========================================================================

const STORAGE_KEY = "mcm_care_gap_reviews.v1";

/** Evento de ventana disparado en cada cambio del store. */
export const CARE_GAP_REVIEWS_EVENT = "mcm:care-gap-reviews-changed";

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage lleno o bloqueado: la alerta vive solo en esta sesión.
  }
  try {
    window.dispatchEvent(new Event(CARE_GAP_REVIEWS_EVENT));
  } catch {
    /* sin window (tests) */
  }
};

/**
 * Revisiones de care gaps registradas, la más reciente primero.
 *
 * @returns {Array<{appointmentId:string, patientName:string, chartNumber:string,
 *   openCareGaps:number, appointmentDate:string, reviewedAt:string}>}
 */
export const getCareGapReviews = () =>
  read().sort((a, b) => String(b.reviewedAt || "").localeCompare(String(a.reviewedAt || "")));

/**
 * Registra (o actualiza, por cita) la revisión de care gaps de un paciente.
 *
 * @param {Object} entry
 * @param {number|string} entry.appointmentId
 * @param {string} entry.patientName
 * @param {string} [entry.chartNumber]
 * @param {number} entry.openCareGaps - care gaps abiertos del paciente
 * @param {string} [entry.appointmentDate]
 * @param {string} [entry.reviewedBy]
 */
export const addCareGapReview = (entry) => {
  const id = String(entry.appointmentId);
  const record = {
    appointmentId: id,
    patientName: entry.patientName || "—",
    chartNumber: entry.chartNumber || "",
    openCareGaps: Number(entry.openCareGaps) || 0,
    appointmentDate: entry.appointmentDate || "",
    reviewedBy: entry.reviewedBy || "",
    reviewedAt: new Date().toISOString(),
  };
  const existing = read().find((r) => r.appointmentId === id);
  // Sin cambios reales no se reescribe: evita disparar el evento en cada
  // render y que la alerta "salte" al tope del panel sola.
  if (existing && existing.openCareGaps === record.openCareGaps) return existing;
  write([record, ...read().filter((r) => r.appointmentId !== id)]);
  return record;
};

/** Quita la revisión (el ítem del checklist se desmarcó o cambió de respuesta). */
export const removeCareGapReview = (appointmentId) => {
  const id = String(appointmentId);
  if (!read().some((r) => r.appointmentId === id)) return;
  write(read().filter((r) => r.appointmentId !== id));
};
